import { useState, useEffect, useMemo, useCallback } from 'react';
import { Monitor, Smartphone, Laptop, WifiOff } from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { formatDistance, parseISO, isPast } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { BackButton } from '@/components/ui/app/back-button';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { toast } from 'sonner';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseDevice(platform, deviceString) {
    const s = (platform ?? '').toLowerCase();

    if (s.includes('mobile')) return { Icon: Smartphone, label: deviceString };

    if (s.includes('desktop')) return { Icon: Laptop, label: deviceString };

    return { Icon: Monitor, label: deviceString };
}

function DeviceCell({ platform, deviceString }) {
    const { Icon, label } = parseDevice(platform, deviceString);
    return (
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-[13px] text-foreground">{label}</span>
        </div>
    );
}

function StatusBadge({ status }) {
    const { t } = useTranslation();
    if (status === 'expired') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-destructive/10 text-destructive text-[12px] font-medium whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive inline-block" />
                {t('devices:sessionExpiree')}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-success text-[12px] font-medium whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
            {t('devices:sessionOuverte')}
        </span>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

function mapTokenToSession(item) {
    const device = [item.user_agent, item.device_name].filter(Boolean).join(' sur ');
    return {
        id: item.uniq_id || item.id,
        user: item.user_name || item.user?.name || item.user_email || i18n.t('devices:moi'),
        device: device || i18n.t('devices:inconnu'),
        platform: item.platform || null,
        company: item.company || null,
        lastActivityRaw: item.last_used_at || null,
        lastActivity: item.last_used_at
            ? formatDistance(parseISO(item.last_used_at), new Date(), { addSuffix: true, locale: fr })
            : i18n.t('devices:jamais'),
        status: item.expires_at && isPast(parseISO(item.expires_at)) ? 'expired' : 'active',
    };
}

export default function DevicesPage() {
    const { t } = useTranslation();
    const { can } = usePermissions();
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [disconnectingId, setDisconnectingId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const fetchSessions = useCallback(async (targetPage) => {
        setIsLoading(true);
        try {
            const response = await horodatyApi.devices.list({
                page: targetPage,
                per_page: PAGE_SIZE,
                order_by: 'last_used_at',
                order: 'desc',
            });
            const payload = response.data;
            const items = Array.isArray(payload.data) ? payload.data : [];
            setSessions(items.map(mapTokenToSession));
            setPageCount(payload.last_page ?? 1);
            setTotalCount(payload.total ?? items.length);
        } catch (error) {
            console.error(error);
            toast.error(t('devices:impossibleDeChargerLesSessions'));
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchSessions(page);
    }, [page, fetchSessions]);

    const handleDisconnect = async (sessionId) => {
        setDisconnectingId(sessionId);
        try {
            await horodatyApi.devices.delete(sessionId);
            // Reload current page to get fresh data
            await fetchSessions(page);
            toast.success(t('devices:sessionDeconnecteeAvecSucces'));
        } catch (error) {
            console.error(error);
            toast.error(t('devices:impossibleDeDeconnecterCetteSession'));
        } finally {
            setDisconnectingId(null);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'user',
                header: t('devices:utilisateur'),
                cell: ({ row }) => <span className="text-[13px] text-foreground font-medium">{row.original.user}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'device',
                header: t('devices:appareil'),
                cell: ({ row }) => <DeviceCell platform={row.original.platform} deviceString={row.original.device} />,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'company',
                header: t('devices:entreprise'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.company ?? '—'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'lastActivity',
                header: t('devices:derniereActivite'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.lastActivity}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'status',
                header: t('devices:statut'),
                cell: ({ row }) => <StatusBadge status={row.original.status} />,
                meta: { hideOnMobile: true },
            },
            {
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <div className="text-right">
                        {can('LIST_DEVICES') && (
                            <Button
                                variant="solid"
                                color="destructive"
                                size="xsm"
                                onClick={() => handleDisconnect(row.original.id)}
                                disabled={disconnectingId === row.original.id}
                            >
                                {t('devices:deconnecter')}
                            </Button>
                        )}
                    </div>
                ),
                meta: { hideOnMobile: true },
            },
            {
                id: 'mobile',
                header: '',
                cell: ({ row }) => (
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-1.5">
                            <p className="text-[14px] font-medium text-foreground">{row.original.user}</p>
                            <DeviceCell platform={row.original.platform} deviceString={row.original.device} />
                            <p className="text-[12px] text-muted-foreground">
                                {row.original.company ?? '—'} · {row.original.lastActivity}
                            </p>
                            <StatusBadge status={row.original.status} />
                        </div>
                        {can('LIST_DEVICES') && (
                            <Button
                                variant="solid"
                                color="destructive"
                                size="xsm"
                                className="shrink-0"
                                onClick={() => handleDisconnect(row.original.id)}
                                disabled={disconnectingId === row.original.id}
                            >
                                {t('devices:deconnecter')}
                            </Button>
                        )}
                    </div>
                ),
                meta: { showOnMobileOnly: true },
            },
        ],
        [disconnectingId, t],
    );

    const activeCount = totalCount;

    if (!can('LIST_DEVICES')) {
        return (
            <>
                <PageHeader title={t('devices:gestionDesAppareils')}>
                    <BackButton />
                </PageHeader>
                <div className="p-8 flex items-center justify-center min-h-[50vh]">
                    <span className="text-[14px] text-muted-foreground">{t('devices:vousNAvezPasAccesACettePage')}</span>
                </div>
            </>
        );
    }

    if (isLoading && sessions.length === 0) {
        return (
            <>
                <PageHeader title={t('devices:gestionDesAppareils')}>
                    <BackButton />
                </PageHeader>
                <div className="p-8 flex items-center justify-center min-h-[50vh]">
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                        <span className="text-[14px]">{t('devices:chargementDesSessions')}</span>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader title={t('devices:gestionDesAppareils')}>
                <BackButton />
            </PageHeader>

            <div className="p-4 md:p-8 pb-[120px] md:pb-8 space-y-6">
                <div className="bg-card rounded-2xl border border-border overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <WifiOff className="w-5 h-5 text-muted-foreground" />
                            <span className="text-[15px] font-semibold text-foreground">{t('devices:sessionsActives')}</span>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-brand/10 text-brand text-[12px] font-semibold">
                            {activeCount} session{activeCount !== 1 ? 's' : ''} ouverte{activeCount !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <DataTable
                        columns={columns}
                        data={sessions}
                        emptyMessage={t('devices:aucuneSessionActive')}
                        emptyIcon={<WifiOff className="w-10 h-10 text-muted-foreground" />}
                        manualPagination
                        pageCount={pageCount}
                        currentPage={page}
                        onPageChange={setPage}
                        pageSize={PAGE_SIZE}
                        totalCount={totalCount}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </>
    );
}
